import React, { useState, useEffect } from "react";
import { Modal, Button, Alert, Spinner, ListGroup, Badge } from "react-bootstrap";
import SchoolRollNumberInput from "./SchoolRollNumberInput";
import { getSchoolGroupById, addSchoolToGroup, removeSchoolFromGroup } from "../services/APIClient";

function ManageGroupSchoolsModal({ show, onHide, group, onMembershipChanged }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // School selected via SchoolRollNumberInput
  const [pendingSchoolId, setPendingSchoolId] = useState(null);
  const [pendingSchoolName, setPendingSchoolName] = useState(null);
  const [addingSchool, setAddingSchool] = useState(false);

  useEffect(() => {
    if (show && group) {
      fetchMembers();
    }
  }, [show, group]);

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSchoolGroupById(group.id);
      setMembers(Array.isArray(data.schools) ? data.schools : []);
    } catch (err) {
      console.error("Error fetching group members:", err);
      setError(`Failed to load group members: ${err.message}`);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolFound = (schoolId) => {
    setPendingSchoolId(schoolId);
  };

  const handleSchoolNameRetrieved = (schoolName) => {
    setPendingSchoolName(schoolName);
  };

  const handleAddSchool = async () => {
    if (!pendingSchoolId) return;

    setAddingSchool(true);
    setError(null);
    try {
      await addSchoolToGroup(group.id, pendingSchoolId);
      setSuccessMessage(`${pendingSchoolName || "School"} added to group`);
      setPendingSchoolId(null);
      setPendingSchoolName(null);
      await fetchMembers();
      onMembershipChanged();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error adding school to group:", err);
      setError(`Failed to add school: ${err.message}`);
    } finally {
      setAddingSchool(false);
    }
  };

  const handleRemoveSchool = async (schoolId, schoolName) => {
    if (!window.confirm(`Remove ${schoolName} from this group?`)) return;

    setError(null);
    try {
      await removeSchoolFromGroup(group.id, schoolId);
      setSuccessMessage(`${schoolName} removed from group`);
      await fetchMembers();
      onMembershipChanged();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error removing school from group:", err);
      setError(`Failed to remove school: ${err.message}`);
    }
  };

  const handleClose = () => {
    setPendingSchoolId(null);
    setPendingSchoolName(null);
    setError(null);
    setSuccessMessage(null);
    onHide();
  };

  const alreadyInGroup = pendingSchoolId && members.some((s) => s.id === pendingSchoolId);

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Manage Schools — <span className="text-primary">{group?.name}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {successMessage && (
          <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)} className="mb-3">
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
            {error}
          </Alert>
        )}

        {/* Current members */}
        <h6 className="fw-semibold mb-2">
          Current Members
          <Badge bg="secondary" className="ms-2">{members.length}</Badge>
        </h6>

        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading members...
          </div>
        ) : members.length === 0 ? (
          <Alert variant="light" className="border mb-3">
            No schools in this group yet.
          </Alert>
        ) : (
          <ListGroup className="mb-4">
            {members.map((school) => (
              <ListGroup.Item
                key={school.id}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <span className="fw-semibold">{school.name}</span>
                  {school.rollnumber && (
                    <span className="text-muted ms-2 small">({school.rollnumber})</span>
                  )}
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveSchool(school.id, school.name)}
                >
                  Remove
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}

        {/* Add a school */}
        <hr />
        <h6 className="fw-semibold mb-3">Add a School</h6>
        <SchoolRollNumberInput
          onSchoolIdRetrieved={handleSchoolFound}
          schoolNameRetrieved={handleSchoolNameRetrieved}
        />

        {pendingSchoolId && (
          <div className="mt-3">
            {alreadyInGroup ? (
              <Alert variant="warning">
                {pendingSchoolName || "This school"} is already a member of this group.
              </Alert>
            ) : (
              <div className="d-flex align-items-center gap-3">
                <span className="text-success fw-semibold">
                  {pendingSchoolName} ready to add
                </span>
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleAddSchool}
                  disabled={addingSchool}
                >
                  {addingSchool ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Adding...
                    </>
                  ) : (
                    "Add to Group"
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ManageGroupSchoolsModal;
